import React, { useEffect, useRef, useState } from "react";
import {
  Loader2, UploadCloud, X, Check, Send, Trash2, Link2, Sparkles, AlertTriangle,
  ImagePlus, ChevronDown, Search,
} from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { uploadMedia, absUrl, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

/* ---------- helpers ---------- */

// Resize/compress in the browser so owners can upload straight from phone/camera.
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob ? new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

// Read a File/Blob into raw base64 (no data: prefix) for the vision analysis call.
function toBase64(fileOrBlob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(fileOrBlob);
  });
}

const ROLES = ["accessory", "centerpiece", "founder"];
const csv = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");
const fromCsv = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

/* ---------- Collection picker (self-contained, array in/out) ---------- */

function DraftCollectionPicker({ value, onChange }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    adminApi.get("/resource/collections").then(({ data }) => setAll(data || [])).catch(() => setAll([]));
  }, []);
  const selected = Array.isArray(value) ? value : [];
  const nameOf = (slug) => all.find((c) => c.slug === slug)?.name || slug;
  const toggle = (slug) => {
    const set = new Set(selected);
    set.has(slug) ? set.delete(slug) : set.add(slug);
    onChange([...set]);
  };
  const ql = q.trim().toLowerCase();
  const filtered = all.filter((c) => !ql || (c.name || "").toLowerCase().includes(ql) || (c.slug || "").toLowerCase().includes(ql));
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap gap-1.5">
        {selected.length === 0 && <span className="text-xs text-wp-ink-muted">No collections yet</span>}
        {selected.map((slug) => (
          <span key={slug} className="inline-flex items-center gap-1 rounded-full bg-wp-blush-100 px-2 py-0.5 text-xs text-wp-rose-strong" data-testid={`draft-collection-chip-${slug}`}>
            {nameOf(slug)}
            <button type="button" onClick={() => toggle(slug)} className="rounded-full hover:text-wp-ink"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-8 rounded-wp-md text-xs" data-testid="draft-collection-trigger">
            <ImagePlus className="mr-1 h-3.5 w-3.5" /> Assign collections <ChevronDown className="ml-1 h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-wp-ink-muted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search collections…" className="h-8 rounded-wp-md pl-7 text-xs" />
          </div>
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {filtered.length === 0 && <p className="px-2 py-3 text-center text-xs text-wp-ink-muted">No collections found</p>}
            {filtered.map((c) => {
              const on = selected.includes(c.slug);
              return (
                <button type="button" key={c.slug} onClick={() => toggle(c.slug)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-wp-blush-50 ${on ? "text-wp-rose-strong" : "text-wp-ink"}`}
                  data-testid={`draft-collection-option-${c.slug}`}>
                  <span className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded border ${on ? "border-wp-rose-strong bg-wp-rose-strong text-white" : "border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
                  <span className="flex-1 truncate">{c.name || c.slug}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/* ---------- Draft card ---------- */

function DraftCard({ draft, selected, onToggleSelect, onChange, onSave, onPublish, onDelete, onAttach, onKeepNew, busy }) {
  const gallery = [draft.image, ...(draft.images || [])].filter(Boolean);
  // Compute needs locally so the Ready/Publish state updates live while editing.
  const needs = [];
  if (draft.price == null || Number(draft.price) <= 0) needs.push("price");
  if (draft.inventory == null || draft.inventory === "") needs.push("inventory");
  if (!draft.category) needs.push("category");
  const ready = needs.length === 0;
  const set = (patch) => onChange({ ...draft, ...patch });

  return (
    <div className="rounded-wp-lg border border-border bg-card p-4 shadow-wp-soft" data-testid={`draft-card-${draft.id}`}>
      <div className="flex flex-col gap-4 md:flex-row">
        {/* Images */}
        <div className="md:w-40 md:flex-shrink-0">
          <label className="mb-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected} onChange={onToggleSelect} className="h-4 w-4 accent-wp-rose-strong" data-testid={`draft-select-${draft.id}`} />
            <span className="text-wp-ink-muted">Select</span>
          </label>
          <div className="aspect-square overflow-hidden rounded-wp-md border border-border bg-wp-cream-100">
            {gallery[0] ? <img src={gallery[0]} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-wp-ink-muted"><ImagePlus className="h-6 w-6" /></div>}
          </div>
          {gallery.length > 1 && (
            <div className="mt-2 flex gap-1.5">
              {gallery.slice(1, 5).map((u, i) => <div key={i} className="h-9 w-9 overflow-hidden rounded border border-border"><img src={u} alt="" className="h-full w-full object-cover" /></div>)}
              {gallery.length > 5 && <div className="grid h-9 w-9 place-items-center rounded border border-border text-xs text-wp-ink-muted">+{gallery.length - 5}</div>}
            </div>
          )}
          {typeof draft.confidence === "number" && (
            <p className="mt-2 text-xs text-wp-ink-muted">Match certainty · {Math.round(draft.confidence * 100)}%</p>
          )}
        </div>

        {/* Fields */}
        <div className="flex-1 space-y-3">
          {/* Match banner */}
          {draft.match && (
            <div className="flex flex-wrap items-center gap-2 rounded-wp-md border border-wp-gold/40 bg-wp-gold/10 px-3 py-2" data-testid={`draft-match-${draft.id}`}>
              <Sparkles className="h-4 w-4 text-wp-gold" />
              <span className="text-sm text-wp-ink">Looks like an existing product: <b>{draft.match.name}</b></span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" className="h-7 rounded-wp-md text-xs" onClick={() => onAttach(draft, draft.match.id)} disabled={busy} data-testid={`draft-attach-${draft.id}`}>
                  <Link2 className="mr-1 h-3.5 w-3.5" /> Add photos to it
                </Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-wp-md text-xs" onClick={() => onKeepNew(draft)} data-testid={`draft-keepnew-${draft.id}`}>Keep as new</Button>
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs">Product name</Label>
              <Input value={draft.name || ""} onChange={(e) => set({ name: e.target.value })} className="h-9 rounded-wp-md" placeholder="e.g. Pearl Drop Earrings" data-testid={`draft-name-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Category</Label>
              <Input value={draft.category || ""} onChange={(e) => set({ category: e.target.value })} className="h-9 rounded-wp-md" placeholder="e.g. Hair Accessories" data-testid={`draft-category-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Role</Label>
              <Select value={draft.role || "accessory"} onValueChange={(v) => set({ role: v })}>
                <SelectTrigger className="h-9 rounded-wp-md" data-testid={`draft-role-${draft.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs">Description</Label>
              <Textarea rows={3} value={draft.description || ""} onChange={(e) => set({ description: e.target.value })} className="rounded-wp-md" placeholder="Warm, gift-oriented description…" data-testid={`draft-description-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Price (₹) <span className="text-wp-rose-strong">*</span></Label>
              <Input type="number" value={draft.price ?? ""} onChange={(e) => set({ price: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 rounded-wp-md" data-testid={`draft-price-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Compare-at (₹)</Label>
              <Input type="number" value={draft.compare_at ?? ""} onChange={(e) => set({ compare_at: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 rounded-wp-md" data-testid={`draft-compareat-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Inventory <span className="text-wp-rose-strong">*</span></Label>
              <Input type="number" value={draft.inventory ?? ""} onChange={(e) => set({ inventory: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 rounded-wp-md" data-testid={`draft-inventory-${draft.id}`} />
            </div>
            <div className="flex items-end">
              <div className="flex w-full items-center justify-between rounded-wp-md border border-border px-3 py-1.5">
                <Label className="text-xs">Personalizable</Label>
                <Switch checked={!!draft.personalizable} onCheckedChange={(v) => set({ personalizable: v })} data-testid={`draft-personalizable-${draft.id}`} />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs">Collections</Label>
              <DraftCollectionPicker value={draft.intent_tags || []} onChange={(v) => set({ intent_tags: v })} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Tags</Label>
              <Input value={csv(draft.tags)} onChange={(e) => set({ tags: fromCsv(e.target.value) })} className="h-9 rounded-wp-md" placeholder="comma, separated" data-testid={`draft-tags-${draft.id}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Colors</Label>
              <Input value={csv(draft.colors)} onChange={(e) => set({ colors: fromCsv(e.target.value) })} className="h-9 rounded-wp-md" placeholder="sage, gold" data-testid={`draft-colors-${draft.id}`} />
            </div>
          </div>

          {/* Needs + actions */}
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {needs.length > 0 ? (
              <Badge className="border-0 bg-wp-blush-100 text-wp-rose-strong" data-testid={`draft-needs-${draft.id}`}>
                <AlertTriangle className="mr-1 h-3 w-3" /> Needs: {needs.join(", ")}
              </Badge>
            ) : (
              <Badge className="border-0 bg-wp-success/15 text-wp-success" data-testid={`draft-ready-${draft.id}`}>
                <Check className="mr-1 h-3 w-3" /> Ready to publish
              </Badge>
            )}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" className="h-8 rounded-wp-md text-red-600 hover:text-red-700" onClick={() => onDelete(draft)} disabled={busy} data-testid={`draft-delete-${draft.id}`}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Discard
              </Button>
              <Button size="sm" variant="outline" className="h-8 rounded-wp-md" onClick={() => onSave(draft)} disabled={busy} data-testid={`draft-save-${draft.id}`}>Save</Button>
              <Button size="sm" className="h-8 rounded-wp-md bg-wp-rose-strong text-white hover:brightness-110 disabled:opacity-50" onClick={() => onPublish(draft)} disabled={busy || !ready} data-testid={`draft-publish-${draft.id}`}>
                <Send className="mr-1 h-3.5 w-3.5" /> Publish
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main component ---------- */

const EDITABLE = ["name", "category", "role", "description", "intent_tags", "tags", "colors", "personalizable", "price", "compare_at", "inventory"];
const pickEditable = (d) => EDITABLE.reduce((acc, k) => { acc[k] = d[k]; return acc; }, {});

// Live needs check (mirrors backend _draft_needs) so UI reflects unsaved edits.
const draftNeeds = (d) => {
  const n = [];
  if (d.price == null || Number(d.price) <= 0) n.push("price");
  if (d.inventory == null || d.inventory === "") n.push("inventory");
  if (!d.category) n.push("category");
  return n;
};
const isReady = (d) => draftNeeds(d).length === 0;

export default function BulkUpload({ open, onOpenChange, onPublished }) {
  const [step, setStep] = useState("upload"); // upload | review
  const [files, setFiles] = useState([]); // {id, file, preview}
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState({}); // id -> bool
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFiles([]); setProgress(""); setAnalyzing(false); };

  // Load any pre-existing drafts when opened so nothing is lost between sessions.
  useEffect(() => {
    if (!open) return;
    setStep("upload"); reset();
    adminApi.get("/bulk/drafts").then(({ data }) => {
      if (Array.isArray(data) && data.length) { setDrafts(data); setStep("review"); }
      else setDrafts([]);
    }).catch(() => setDrafts([]));
  }, [open]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming.map((f) => ({ id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`, file: f, preview: URL.createObjectURL(f) }))]);
  };
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const analyze = async () => {
    if (!files.length) return toast.error("Add a few product photos first");
    setAnalyzing(true);
    try {
      const images = [];
      for (let i = 0; i < files.length; i++) {
        setProgress(`Uploading photo ${i + 1} of ${files.length}…`);
        const compressed = await compressImage(files[i].file);
        const data = await uploadMedia(compressed, "photo", "admin");
        const b64 = await toBase64(compressed);
        images.push({ url: absUrl(data.media_url), b64 });
      }
      setProgress("Reading your photos…");
      const { data } = await adminApi.post("/bulk/analyze", { images });
      const newDrafts = data?.drafts || [];
      setDrafts((prev) => [...newDrafts, ...prev]);
      setStep("review");
      reset();
      toast.success(`Created ${newDrafts.length} draft${newDrafts.length === 1 ? "" : "s"} for review`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze photos");
    } finally {
      setAnalyzing(false);
      setProgress("");
    }
  };

  const patchLocal = (updated) => setDrafts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));

  const saveDraft = async (draft, silent = false) => {
    setBusyId(draft.id);
    try {
      const { data } = await adminApi.put(`/bulk/drafts/${draft.id}`, pickEditable(draft));
      patchLocal({ ...draft, ...data });
      if (!silent) toast.success("Draft saved");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save draft");
      throw e;
    } finally {
      setBusyId(null);
    }
  };

  const publishDraft = async (draft) => {
    setBusyId(draft.id);
    try {
      await adminApi.put(`/bulk/drafts/${draft.id}`, pickEditable(draft)); // persist edits first
      await adminApi.post(`/bulk/drafts/${draft.id}/publish`);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      setSelected((s) => { const n = { ...s }; delete n[draft.id]; return n; });
      toast.success(`Published “${draft.name || "product"}”`);
      onPublished && onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally {
      setBusyId(null);
    }
  };

  const deleteDraft = async (draft) => {
    setBusyId(draft.id);
    try {
      await adminApi.delete(`/bulk/drafts/${draft.id}`);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      setSelected((s) => { const n = { ...s }; delete n[draft.id]; return n; });
      toast.success("Draft discarded");
    } catch {
      toast.error("Could not discard draft");
    } finally {
      setBusyId(null);
    }
  };

  const attachDraft = async (draft, productId) => {
    setBusyId(draft.id);
    try {
      await adminApi.post(`/bulk/drafts/${draft.id}/attach`, { product_id: productId });
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      toast.success("Photos added to existing product");
      onPublished && onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach photos");
    } finally {
      setBusyId(null);
    }
  };

  const keepAsNew = (draft) => patchLocal({ ...draft, match: null });

  const readyDrafts = drafts.filter(isReady);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const publishSelected = async () => {
    const toPublish = drafts.filter((d) => selected[d.id] && isReady(d));
    if (!toPublish.length) return toast.error("Select drafts that have price, inventory and category filled");
    setBulkBusy(true);
    let ok = 0;
    for (const d of toPublish) {
      try {
        await adminApi.put(`/bulk/drafts/${d.id}`, pickEditable(d));
        await adminApi.post(`/bulk/drafts/${d.id}/publish`);
        ok += 1;
      } catch { /* keep going */ }
    }
    setDrafts((prev) => prev.filter((d) => !(selected[d.id] && isReady(d))));
    setSelected({});
    setBulkBusy(false);
    toast.success(`Published ${ok} product${ok === 1 ? "" : "s"}`);
    onPublished && onPublished();
  };

  const allSelected = drafts.length > 0 && drafts.every((d) => selected[d.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(drafts.map((d) => [d.id, true])));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[98vw] max-w-6xl flex-col gap-0 overflow-hidden p-0" data-testid="bulk-upload-dialog">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border bg-wp-cream-100 px-5 py-4">
          <div>
            <h2 className="font-display text-xl text-wp-rose-strong">Smart Bulk Upload</h2>
            <p className="text-sm text-wp-ink-muted">Upload many photos, review the suggested details, then publish the ones you like.</p>
          </div>
          <div className="flex items-center gap-2">
            {step === "review" && (
              <Button variant="outline" size="sm" className="rounded-wp-md" onClick={() => setStep("upload")} data-testid="bulk-add-more">
                <UploadCloud className="mr-1.5 h-4 w-4" /> Add more photos
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => onOpenChange(false)} data-testid="bulk-close"><X className="h-5 w-5" /></Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === "upload" ? (
            <div className="mx-auto max-w-2xl">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                className={`flex cursor-pointer flex-col items-center justify-center rounded-wp-lg border-2 border-dashed px-6 py-14 text-center transition ${dragOver ? "border-wp-rose-strong bg-wp-cream-100" : "border-border bg-card hover:border-wp-rose"}`}
                data-testid="bulk-dropzone"
              >
                <div className="mb-3 grid h-14 w-14 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><UploadCloud className="h-7 w-7" /></div>
                <p className="font-medium text-wp-ink">Drag &amp; drop product photos here</p>
                <p className="mt-1 text-sm text-wp-ink-muted">or click to choose files · photos of the same product will be grouped automatically</p>
                <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-file-input" />
              </div>

              {files.length > 0 && (
                <>
                  <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                    {files.map((f) => (
                      <div key={f.id} className="group relative aspect-square overflow-hidden rounded-wp-md border border-border">
                        <img src={f.preview} alt="" className="h-full w-full object-cover" />
                        <button type="button" onClick={() => removeFile(f.id)} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/55 text-white opacity-0 transition group-hover:opacity-100" data-testid={`bulk-remove-${f.id}`}><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <p className="text-sm text-wp-ink-muted">{files.length} photo{files.length === 1 ? "" : "s"} ready {analyzing && progress && <span>· {progress}</span>}</p>
                    <div className="flex gap-2">
                      <Button variant="ghost" className="rounded-wp-md" onClick={() => setFiles([])} disabled={analyzing} data-testid="bulk-clear">Clear</Button>
                      <Button className="rounded-wp-md bg-wp-rose-strong text-white hover:brightness-110" onClick={analyze} disabled={analyzing} data-testid="bulk-analyze">
                        {analyzing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Working…</> : <><Sparkles className="mr-2 h-4 w-4" /> Suggest details for {files.length} photo{files.length === 1 ? "" : "s"}</>}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {drafts.length > 0 && (
                <div className="mt-6 text-center">
                  <Button variant="link" className="text-wp-rose-strong" onClick={() => setStep("review")} data-testid="bulk-goto-review">
                    You have {drafts.length} draft{drafts.length === 1 ? "" : "s"} waiting for review →
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              {drafts.length === 0 ? (
                <div className="grid place-items-center py-20 text-center">
                  <div className="mb-3 grid h-14 w-14 place-items-center rounded-full bg-wp-cream-100 text-wp-rose"><ImagePlus className="h-7 w-7" /></div>
                  <p className="font-medium text-wp-ink">No drafts yet</p>
                  <p className="text-sm text-wp-ink-muted">Add some photos to get started.</p>
                  <Button className="mt-4 rounded-wp-md bg-wp-rose-strong text-white" onClick={() => setStep("upload")} data-testid="bulk-empty-add">Add photos</Button>
                </div>
              ) : (
                <>
                  <div className="mb-4 flex flex-wrap items-center gap-3 rounded-wp-md border border-border bg-card px-4 py-2.5">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-wp-rose-strong" data-testid="bulk-select-all" />
                      Select all
                    </label>
                    <span className="text-sm text-wp-ink-muted">{drafts.length} draft{drafts.length === 1 ? "" : "s"} · {readyDrafts.length} ready</span>
                    <div className="ml-auto">
                      <Button size="sm" className="rounded-wp-md bg-wp-rose-strong text-white hover:brightness-110 disabled:opacity-50" onClick={publishSelected} disabled={bulkBusy || selectedIds.length === 0} data-testid="bulk-publish-selected">
                        {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Publish selected
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {drafts.map((d) => (
                      <DraftCard
                        key={d.id}
                        draft={d}
                        selected={!!selected[d.id]}
                        onToggleSelect={() => setSelected((s) => ({ ...s, [d.id]: !s[d.id] }))}
                        onChange={patchLocal}
                        onSave={(dr) => saveDraft(dr)}
                        onPublish={publishDraft}
                        onDelete={deleteDraft}
                        onAttach={attachDraft}
                        onKeepNew={keepAsNew}
                        busy={busyId === d.id || bulkBusy}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
